import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ExternalLink, Trash2, Mail, CheckCircle2, Send, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const itDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const itDateTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "");
const TOOL = { consulenza: "Consulenza", bolletta: "Bolletta", tetto: "Tetto", colonnina: "Colonnina" };

const summaryText = (s) => {
  if (!s) return "—";
  if (typeof s === "string") return s || "—";
  if (typeof s === "object") {
    const parts = Object.entries(s)
      .filter(([k, v]) => v != null && v !== "" && k !== "disposizione_falde")
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
    return parts.length ? parts.join(" · ") : "—";
  }
  return String(s);
};

// estrae le falde con matrice dal riepilogo del configuratore
const getFalde = (s) => {
  const df = s && typeof s === "object" ? s.disposizione_falde : null;
  if (!Array.isArray(df)) return [];
  return df.filter((f) => Array.isArray(f?.matrice) && f.matrice.length);
};

// griglia visiva del layout pannelli (rispecchiata dx/sx per coerenza con la vista satellitare)
function MatriceMini({ matrice, faldaId }) {
  const rows = matrice.map((r) => [...r].reverse());
  const cols = Math.max(...rows.map((r) => r.length), 1);
  const cell = cols > 24 ? 6 : cols > 16 ? 8 : 10;
  return (
    <div className="mt-1" data-testid={`pratica-matrice-${faldaId || "campo"}`}>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Layout campo{faldaId && faldaId !== "campo" ? ` · ${faldaId}` : ""}</div>
      <div className="inline-flex flex-col gap-[2px] rounded-md border border-border bg-muted/30 p-1.5">
        {rows.map((r, ri) => (
          <div key={ri} className="flex gap-[2px]">
            {r.map((v, ci) => (
              <div key={ci} style={{ width: cell, height: cell }}
                className={v ? "rounded-[2px] bg-primary" : "rounded-[2px] bg-border/40"} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PraticheConfiguratore() {
  const [data, setData] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [mailDlg, setMailDlg] = useState(null); // { config, email, requireEmail }
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get("/rete/configs").then((r) => setData(r.data)).catch(() => setData({ count: 0, configs: [] }));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Eliminare definitivamente questa consulenza${c.installer?.ragione_sociale ? ` di ${c.installer.ragione_sociale}` : ""}? L'operazione la cancella anche dal configuratore.`)) return;
    setDeleting(c.id);
    try {
      await api.delete(`/rete/configs/${c.id}`);
      toast.success("Consulenza eliminata");
      setData((d) => ({ ...d, configs: (d.configs || []).filter((x) => x.id !== c.id), count: Math.max(0, (d.count || 1) - 1) }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile eliminare la consulenza");
    } finally {
      setDeleting(null);
    }
  };

  const openMail = (c) => setMailDlg({ config: c, email: c.consumer?.email || c.email_sent_to || "", requireEmail: false });

  const patchConfig = (id, patch) => setData((d) => ({ ...d, configs: (d.configs || []).map((x) => (x.id === id ? { ...x, ...patch } : x)) }));

  const sendEmail = async () => {
    const c = mailDlg.config;
    const body = mailDlg.email ? { email: mailDlg.email.trim() } : {};
    setSending(true);
    try {
      const r = await api.post(`/rete/configs/${c.id}/send-email`, body);
      toast.success(`Email inviata al cliente finale: ${r.data?.to || mailDlg.email || "—"}`);
      patchConfig(c.id, { email_sent_at: new Date().toISOString(), email_sent_to: r.data?.to || mailDlg.email, email_sent_cc: r.data?.cc });
      setMailDlg(null);
    } catch (e) {
      const st = e.response?.status;
      if (st === 422) {
        setMailDlg((m) => ({ ...m, requireEmail: true }));
        toast.error("Email cliente mancante o non valida: inseriscila e reinvia");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
      }
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    const c = mailDlg.config;
    setTesting(true);
    try {
      await api.post(`/rete/configs/${c.id}/send-email-test`);
      toast.success("Email di prova inviata all'agente");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio prova non riuscito");
    } finally {
      setTesting(false);
    }
  };

  const configs = data?.configs || [];
  return (
    <div className="space-y-6" data-testid="pratiche-configuratore-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Consulenze alla rete</h1>
        <p className="text-sm text-muted-foreground">Consulenze create tramite configuratore torri.solar, lette in sola lettura.</p>
      </div>
      <Card className="p-4">
        {data === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Installatore</TableHead>
                  <TableHead>Agente</TableHead>
                  <TableHead>Strumento</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Riepilogo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Link</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((c) => {
                  const completata = c.status === "completata";
                  return (
                  <TableRow key={c.id} data-testid={`pratica-row-${c.id}`}>
                    <TableCell className="text-sm">{itDate(c.created_at)}</TableCell>
                    <TableCell className="text-sm font-semibold">{c.installer?.ragione_sociale || c.installer?.name || "—"}{c.is_partner ? <Badge variant="secondary" className="ml-2 text-[10px]">Partner</Badge> : null}</TableCell>
                    <TableCell className="text-sm">{c.agent?.name || "—"}</TableCell>
                    <TableCell className="text-sm">{c.tool_label || TOOL[c.tool] || c.tool || "—"}</TableCell>
                    <TableCell className="text-sm">{c.status || "—"}</TableCell>
                    <TableCell className="max-w-xs text-sm text-muted-foreground align-top">
                      {(() => { const txt = summaryText(c.summary); const falde = getFalde(c.summary);
                        if (txt === "—" && !falde.length) return <span>—</span>;
                        const open = !!expanded[c.id];
                        return (
                          <div>
                            <button onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))} data-testid={`pratica-summary-toggle-${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Dettagli
                            </button>
                            {open && (
                              <div className="mt-1">
                                {txt !== "—" && <div className="whitespace-pre-line text-xs leading-relaxed" data-testid={`pratica-summary-text-${c.id}`}>{txt}</div>}
                                {falde.map((f, i) => <MatriceMini key={i} matrice={f.matrice} faldaId={f.falda_id} />)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`pratica-email-cell-${c.id}`}>
                      {c.email_sent_at ? (
                        <span className="inline-flex flex-col leading-tight">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /> Inviata</span>
                          <span className="text-[11px] text-muted-foreground">{itDateTime(c.email_sent_at)}{c.email_sent_to ? ` · ${c.email_sent_to}` : ""}</span>
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">{c.public_token ? <a href={`https://configuratore.torri.solar/rete/p/${c.public_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary hover:underline"><ExternalLink className="h-4 w-4" /></a> : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        {c.installer?.id && c.tool && <Link to={`/configuratore/${c.installer.id}?tool=${c.tool || ""}&config_id=${encodeURIComponent(c.id)}`} title="Riapri la configurazione nel configuratore" data-testid={`pratica-reopen-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-primary"><RotateCcw className="h-4 w-4" /></Link>}
                        <button onClick={() => openMail(c)} disabled={!completata} title={completata ? "Invia la consulenza al cliente finale via email" : "Disponibile solo per consulenze completate"} data-testid={`pratica-email-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-30">
                          <Mail className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(c)} disabled={deleting === c.id} title="Elimina consulenza" data-testid={`pratica-delete-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40">
                          {deleting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {configs.length === 0 && <TableRow><TableCell colSpan={9} className="py-12 text-center text-muted-foreground">Nessuna consulenza ancora presente.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!mailDlg} onOpenChange={(o) => { if (!o) setMailDlg(null); }}>
        <DialogContent data-testid="pratica-email-dialog">
          <DialogHeader>
            <DialogTitle>Invia la consulenza al cliente finale</DialogTitle>
          </DialogHeader>
          {mailDlg && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                L'email viene inviata al <b>cliente finale</b>{mailDlg.config.consumer?.name ? <> (<b>{mailDlg.config.consumer.name}</b>)</> : ""}, cioè il destinatario della consulenza. In <b>copia (CC)</b> ricevono l'installatore <b>{mailDlg.config.installer?.ragione_sociale || "—"}</b> e l'agente. L'invio è solo manuale.
              </p>
              <div>
                <Label htmlFor="mail-to">Email del cliente finale {mailDlg.requireEmail && <span className="text-destructive">*</span>}</Label>
                <Input id="mail-to" type="email" value={mailDlg.email} onChange={(e) => setMailDlg((m) => ({ ...m, email: e.target.value }))} placeholder="cliente.finale@dominio.it" data-testid="pratica-email-input" />
                {mailDlg.requireEmail && <p className="mt-1 text-xs text-destructive">Email del cliente finale mancante o non valida: inseriscila per procedere.</p>}
                <p className="mt-1 text-[11px] text-muted-foreground">Se lasci il campo vuoto viene usata l'email del cliente finale registrata nella consulenza.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={sendTest} disabled={testing || sending} data-testid="pratica-email-test-btn">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Invia una prova a me (agente)
            </Button>
            <Button onClick={sendEmail} disabled={sending || (mailDlg?.requireEmail && !mailDlg?.email.trim())} data-testid="pratica-email-send-btn">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Invia al cliente finale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
